import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {LoadingButton} from "@mui/lab";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    useMediaQuery
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {
    AutomationDomain,
    type AutomationDomainValue,
    type AutomationFlow
} from "crqlar-react-common/dist/models/automation/automation";
import {type AutomationTemplateDefinition} from "crqlar-react-common/dist/models/automation/automationTemplate.model";
import {AutomationService} from "crqlar-react-common/dist/services/automation/AutomationService";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {useNavigate, useParams} from "react-router-dom";
import ReactFlow, {Background, BackgroundVariant, MarkerType, ReactFlowProvider} from "reactflow";
import "reactflow/dist/style.css";
import CustomNode from "../components/CustomNode";
import InsertableEdge from "../components/InsertableEdge";
import {toFlowEdges, toFlowNodes} from "../utils/flowGraph";
import AutomationTemplatePicker, {BLANK_TEMPLATE_KEY, blankAutomationDraft} from "./AutomationTemplatePicker";

export const AUTOMATION_TEMPLATES_QUERY_KEY = 'automation-templates';

/** Stable empty catalogue, so a still-loading query does not hand the picker a new array every render. */
const NO_TEMPLATES: AutomationTemplateDefinition[] = [];

const nodeTypes = {
    custom: CustomNode
};

const edgeTypes = {
    insertable: InsertableEdge
};

interface AutomationCreateDialogProps {
    open: boolean;
    onClose: () => void;
    /** Also decides where the dialog navigates after creating. */
    domain?: AutomationDomainValue;
}

const DialogField = ({label, hint, children}: {label: string, hint: string, children: React.ReactNode}) => (
    <Stack gap={0.75}>
        <Stack direction={'row'} alignItems={'center'}>
            <Typography variant={'body2'} fontWeight={500}>
                {label}
            </Typography>
            <Tooltip title={hint} placement={'right'} componentsProps={{tooltip: {sx: {fontSize: '0.85rem'}}}}>
                <InfoOutlinedIcon
                    sx={{color: 'info.main', cursor: 'help', ml: 0.5, fontSize: '1.2rem', verticalAlign: 'middle'}}
                />
            </Tooltip>
        </Stack>
        {children}
    </Stack>
);

const AutomationCreateDialog = ({open, onClose, domain = AutomationDomain.MARKETING}: AutomationCreateDialogProps) => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const {appId} = useParams<"appId">();
    const readOnly = useReadOnly();

    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const showPreview = useMediaQuery(theme.breakpoints.up('md'));

    // Keyed on the language: the API builds each catalogue's copy in one language.
    const {data: templates = NO_TEMPLATES, isLoading: templatesLoading} = useQuery({
        queryKey: [AUTOMATION_TEMPLATES_QUERY_KEY, domain, i18n.language],
        queryFn: () => AutomationService.getTemplates(domain, i18n.language),
        enabled: open,
        retry: false
    });

    const [selectedKey, setSelectedKey] = useState(BLANK_TEMPLATE_KEY);
    const [canvasReady, setCanvasReady] = useState(false);
    const [nameIsSuggestion, setNameIsSuggestion] = useState(true);

    const automationData = useEntity<AutomationFlow>({
        get: async () => ({
            ...blankAutomationDraft(),
            name: t('New empty automation'),
        }),
        // A blank flow is posted as it stands; a template is created from its key alone (the API mints ids and
        // finds or creates its audiences). The template nodes on screen are only a preview, never saved.
        create: (automation) => selectedKey === BLANK_TEMPLATE_KEY
            ? AutomationService.createFlow(automation)
            : AutomationService.instantiateTemplate({
                templateKey: selectedKey,
                language: i18n.language,
                name: automation.name,
                description: automation.description,
            }),
        update: (automation) => AutomationService.updateFlow(automation.id!, automation),
        isValid: (automation) => !!automation.name?.trim(),
        cleanupBeforeSave: (automation) => ({
            ...automation,
            domain,
            name: automation.name?.trim() || undefined,
            description: automation.description?.trim() || undefined,
        }),
        onSaved: (automation) => {
            onClose();
            const basePath = domain === AutomationDomain.SURVEY ? '/surveys/automations' : '/marketing/automations';
            if (appId && automation.id) navigate(`${basePath}/${appId}/${automation.id}`);
        },
    }, "new");

    const automation = automationData.item;

    const busy = automationData.saving;

    const safeClose = () => {
        if (!busy) onClose();
    };

    const saveNow = () => {
        if (!automationData.isValid || readOnly || busy) return;
        void automationData.save();
    };

    const nameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const timer = window.setTimeout(() => {
            nameRef.current?.focus();
            nameRef.current?.select();
        }, 250);
        return () => window.clearTimeout(timer);
    }, []);

    const nameMissing = !automation.name?.trim();

    const form = (
        <Stack gap={3}>
            <Stack gap={2}>
                <DialogField
                    label={`${t('Name')} *`}
                    hint={t('Internal name of this automation, used to find it in lists and reports.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        inputRef={nameRef}
                        value={automation.name || ''}
                        onChange={(e) => {
                            const name = e.target.value;
                            setNameIsSuggestion(false);
                            automationData.setItem(previous => ({...previous, name}));
                        }}
                        onFocus={(e) => (e.target as HTMLInputElement).select()}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') saveNow();
                        }}
                        fullWidth
                        required
                        disabled={readOnly}
                        error={nameMissing}
                        // Reserve helper-text height to avoid a validation layout shift.
                        helperText={nameMissing ? t('Name is required') : ' '}
                    />
                </DialogField>

                <DialogField
                    label={t('Description')}
                    hint={t('Optional. Shown as an extra column in the automations list, to say what this flow is for.')}
                >
                    <TextField
                        id={'description'}
                        size={'small'}
                        value={automation.description || ''}
                        onChange={(e) => {
                            const description = e.target.value;
                            automationData.setItem(previous => ({...previous, description}));
                        }}
                        multiline
                        minRows={3}
                        fullWidth
                        disabled={readOnly}
                    />
                </DialogField>
            </Stack>

            <AutomationTemplatePicker
                templates={templates}
                automation={automation}
                onChange={automationData.setItem}
                selectedKey={selectedKey}
                onSelectedKeyChange={setSelectedKey}
                nameIsSuggestion={nameIsSuggestion}
                onNameIsSuggestionChange={setNameIsSuggestion}
                // Locked while the catalogue loads, or the menu flashes "No templates available".
                disabled={busy || readOnly || templatesLoading}
            />
        </Stack>
    );

    const edgeDefaults = useMemo(() => ({
        type: 'insertable',
        animated: true,
        style: {stroke: theme.palette.grey[400], strokeWidth: 2},
        markerEnd: {
            type: MarkerType.ArrowClosed,
            color: theme.palette.grey[400],
            width: 18,
            height: 18,
        },
    }), [theme]);

    const previewNodes = useMemo(() => toFlowNodes(automation.nodes), [automation.nodes]);
    const previewEdges = useMemo(
        () => toFlowEdges(automation.connections, edgeDefaults),
        [automation.connections, edgeDefaults]
    );

    const formColumn = (
        <Box
            sx={{
                flex: showPreview ? '1 1 45%' : 1,
                minWidth: 0,
                overflowY: 'auto',
                py: 3,
                pr: showPreview ? 1 : 0,
            }}
        >
            {form}
        </Box>
    );

    const previewColumn = (
        <Box sx={{flex: '1 1 55%', minWidth: 0, py: 3, display: 'flex', flexDirection: 'column'}}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    // A picture of the template, not a canvas: nothing in here takes pointer input.
                    pointerEvents: 'none',
                }}
            >
                {!canvasReady && (
                    <Box sx={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CircularProgress size={28}/>
                    </Box>
                )}
                {canvasReady && <ReactFlowProvider key={selectedKey}>
                    <ReactFlow
                        nodes={previewNodes}
                        edges={previewEdges}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        fitView
                        fitViewOptions={{padding: 0.15, maxZoom: 1}}
                        minZoom={0.2}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        nodesFocusable={false}
                        edgesFocusable={false}
                        panOnDrag={false}
                        zoomOnScroll={false}
                        zoomOnPinch={false}
                        zoomOnDoubleClick={false}
                        preventScrolling={false}
                        proOptions={{hideAttribution: true}}
                    >
                        <Background variant={BackgroundVariant.Dots} gap={12} size={1}/>
                    </ReactFlow>
                </ReactFlowProvider>}

                {selectedKey === BLANK_TEMPLATE_KEY && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 3,
                            pointerEvents: 'none',
                        }}
                    >
                        <Typography variant={'body2'} color={'text.secondary'} textAlign={'center'}>
                            {t('Start from scratch on an empty canvas.')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={safeClose}
            maxWidth={'lg'}
            fullWidth
            fullScreen={fullScreen}
            PaperProps={{sx: fullScreen ? {} : {height: '85vh', maxHeight: '85vh'}}}
            aria-labelledby={'create-automation-dialog-title'}
            TransitionProps={{onEntered: () => setCanvasReady(true)}}
        >
            <DialogTitle sx={{pb: 1}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
                    <Typography id={'create-automation-dialog-title'} variant={'h6'} fontWeight={600} noWrap>
                        {t('Create automation')}
                    </Typography>
                    <IconButton onClick={safeClose} size={'small'} aria-label={t('Close')}>
                        <CloseIcon/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers sx={{px: 3, py: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
                {showPreview
                    ? (
                        <Stack direction={'row'} gap={3} sx={{flex: 1, minHeight: 0, minWidth: 0}}>
                            {formColumn}
                            {previewColumn}
                        </Stack>
                    )
                    : formColumn}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={safeClose} disabled={busy}>
                    {t('Cancel')}
                </Button>
                <LoadingButton
                    id={'btn-entity-save'}
                    variant={'contained'}
                    onClick={saveNow}
                    loading={busy}
                    disabled={!automationData.isValid || readOnly}
                >
                    {t('Create automation')}
                </LoadingButton>

                {automationData.renderSnackbars()}
            </DialogActions>
        </Dialog>
    );
};

export default AutomationCreateDialog;
